<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="reg-merge-modal">
    <UtdModalDialog ref="regMergeModal"
                    modal-classes="reg-merge__modal"
                    :dialog-type="'successClean'"
                    :close-on-overlay-click="false"
                    :close-on-escape="true"
                    :hide-header-close="true">
      <template #header>
        <i class="wkce-icon-filled-check-circle" />
        <span class="reg-merge__modal-header">Success!</span>
      </template>
      <template v-slot>
        <div class="reg-merge__modal-content">
          <p class="reg-merge__modal-message">{{ successMessage().heading }}</p>
          <div class="reg-merge__modal-text-content">
            <p class="reg-merge__modal-text" v-html="successMessage().message" />
          </div>
          <div v-if="showEditProfileSection">
            <hr class="reg-merge__separator">
            <div class="reg-merge__edit-profile">
              <p v-if="isConsolidateMerge">
                <span class="reg-merge__label">
                  If you want to edit your UpToDate
                </span> account details, select "Edit My Profile" below.
              </p>
              <p v-else>
                <span class="reg-merge__label">
                  Please review your UpToDate
                </span> account details below and select "Edit My Profile"
                to update if needed.
              </p>
              <UtdButton id="editProfileButton"
                         button-style="text"
                         button-icon="pencil"
                         class="form-button edit-profile-button"
                         @click="handleEditProfileClick">Edit My Profile
              </UtdButton>
            </div>
            <MergeAccountInfo v-if="showMergeAccountInfo" 
                              :merge-profile="automergeProfile"
                              class="ds1-mt-2 " />
          </div>
        </div>
      </template>
      <template #footer>
        <UtdButton id="closeButton"
                   button-size="small"
                   button-style="clean"
                   button-color="green"
                   class="form-button close-button"
                   @click="closeDialog()">OK
        </UtdButton>
      </template>
    </UtdModalDialog>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';
import { useStore } from 'vuex';
import UtdModalDialog from '_acaSrc/components/shared/stdlib/UtdModalDialog.vue';
import UtdButton from '_acaSrc/components/shared/stdlib/UtdButton.vue';
import MergeAccountInfo from '_acaSrc/components/register/MergeAccountInfo.vue';
import PubSub from '_acaSrc/utility/PubSub';
import useRegistrationForm from '_acaSrc/composables/registrationForm.composable';
import useRegisterMergeStatus from '_acaSrc/composables/registerMergeStatus.composable';
import { setQueryParamValueNoReload } from '_acaSrc/utility/Browsers';
import { C_EVENTS, C_MY_ACCOUNT, C_REG_MERGE } from '_acaSrc/utility/constants';
import { SET_MYACCOUNT_SIDEBAR_REQUEST } from '_acaSrc/store/app.store';

const {
    successMessage,
    clearSuccessMessage
} = useRegistrationForm();

const { mergeType, mergeSource, MERGE_SOURCES, resetMergeStatus } = useRegisterMergeStatus();

const showEditProfileSection = computed(() => {
    return mergeType.value !== C_REG_MERGE.MESSAGES.REG.name 
        && mergeType.value !== C_REG_MERGE.MESSAGES.BYOL_REG.name;
});

const pubsub = new PubSub();

onMounted(() => {
    pubsub.subscribe('showSuccessNotification', () => {
        showSuccessNotification();
    });
});

onUnmounted(() => {
    pubsub.unsubscribe('showSuccessNotification');
});

const regMergeModal = ref(null);
const showSuccessNotification = () => {
    if (mergeType.value === C_REG_MERGE.MESSAGES.REG.name) {
        return;
    }
    regMergeModal.value && regMergeModal.value.open();
};

const closeDialog = () => {
    regMergeModal.value.close();
    setQueryParamValueNoReload(C_REG_MERGE.REG_MERGE_SUCCESS_PARAM);
    clearSuccessMessage();
    resetMergeStatus();
};

const store = useStore();
const isConsolidateMerge = computed(() => mergeSource.value === MERGE_SOURCES.CONSOLIDATE);
const isSsoToSsoAutoMergeVisible = computed(() => 
    store.getters['feature/isSsoToSsoAutoMergeEnabled']
);

const automergeProfile = computed(() => store.getters['login/mergeProfile']);

const showMergeAccountInfo = computed(() => {
    return !!automergeProfile.value
        && isSsoToSsoAutoMergeVisible.value
        && !isConsolidateMerge.value;
});

const waitForNextFrame = () => new Promise(resolve => requestAnimationFrame(resolve));

const handleEditProfileClick = async() => {
    const unwatch = store.watch(
        (state) => state.app.myAccountSidebarRequestStatus,
        async(status) => {
            if (status === C_MY_ACCOUNT.SIDEBAR_REQUEST.SHOWN) {
                await waitForNextFrame();
                pubsub.publish(C_EVENTS.OPEN_CONTACT_INFO_MODAL, { shouldScroll: false });
                unwatch();
            }
        }
    );
    
    store.commit(`app/${SET_MYACCOUNT_SIDEBAR_REQUEST}`, C_MY_ACCOUNT.SIDEBAR_REQUEST.APPROVED);
    closeDialog();    
};
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.reg-merge__modal-header {
  font-size: 22px;
  font-weight: 700;
  padding: 0;
}

.reg-merge__modal-content {
  .ds1-mh-7();
  .ds1-mb-4();

  b {
    .ds1-utd-weight-600();
  }
}

.reg-merge__modal-message {
  .ds1-utd-weight-600();
  .ds1-mt-0();
}

.reg-merge__modal-text {
  .ds1-mv-2();
  font-size: 14px;
  max-width: 480px;
}

.reg-merge__separator {
  .ds1-mv-3();
  border: none;
  border-top: @DS1-UTD-GRAY-SINGLE-BORDER;
  margin-right: 0;
}

.reg-merge__edit-profile {
  font-size: 14px;
}

.reg-merge__label {
  .ds1-utd-weight-600();
}

#closeButton {
  .ds1-utd-weight-600();
}

#editProfileButton {
  .ds1-pa-0();
}
</style>

<style lang="less">
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

@media screen and (min-width: 768px) {
  .reg-merge__modal .utd-modal-content .utd-dialog-frame {
    max-width: 480px;
  }
}

.reg-merge__modal.utd-modal-overlay {
  z-index: @ZINDEX-MODAL-OVERLAY-REG-MERGE;
}

.reg-merge__modal-text-list {
  .ds1-mb-4();
  .ds1-pl-3();
  max-width: 480px;
  font-size: 14px;
  width: 85%;
}

.reg-merge__modal-text-list-item {
  margin-bottom: 12px;
  line-height: 1.6;
}
</style>